import { useRef, useEffect, useCallback } from 'react';
import { useMenuDispatch, useMenuState } from '@olo-web/client-state';
import { useIsClient } from '@olo-web/utils/common/hooks/useIsClient';
import { useMenuFilteredBySearch } from '@olo-web/domain/menu/queries/useMenu';
import throttle from 'lodash/throttle';
import { useScreenSizeState } from '@olo-web/client-state';

type TProps = {
  footerInView: boolean;
};

export const useMenuGroupScrollListener = ({ footerInView }: TProps) => {
  const { selectedGroupId, isProgrammaticScrolling } = useMenuState();
  const menuDispatch = useMenuDispatch();
  const isClient = useIsClient();
  const { groups } = useMenuFilteredBySearch();

  const { isMdDown } = useScreenSizeState();

  const groupRefs = useRef({});
  const selectedGroupIdRef = useRef(selectedGroupId);
  const isProgrammaticScrollingRef = useRef(isProgrammaticScrolling);

  // Pixels of sticky/fixed nav-bar covering the top of the scroll container.
  // Mobile/tablet (<md=1188): ScrollSensitiveNavBar height (98px) which embeds the ribbon when scrolled.
  // Desktop (>=md): sticky ribbon (top:63 + h:44 = 107) inside MenuHeader.
  const SCROLL_THRESHOLD = isMdDown ? 98 : 107;

  useEffect(() => {
    selectedGroupIdRef.current = selectedGroupId;
  }, [selectedGroupId]);

  useEffect(() => {
    isProgrammaticScrollingRef.current = isProgrammaticScrolling;
  }, [isProgrammaticScrolling]);

  const updateSelectedGroup = (groupId: string): void => {
    // Re-check the flag at dispatch time so a throttle's trailing call that was
    // queued during a programmatic scroll doesn't override the user's selection.
    if (isProgrammaticScrollingRef.current) return;
    menuDispatch({ type: 'UPDATE_SELECTED_GROUP', payload: groupId });
  };

  const updateSelectedGroupThrottled = throttle(updateSelectedGroup, 400, {
    trailing: true,
  });

  const handleScroll = useCallback(
    (e) => {
      // While a tab click is smooth-scrolling the page, the click handler is
      // authoritative for the selected group; the scroll-spy must not race it.
      if (isProgrammaticScrollingRef.current) return;

      const SCROLL_TOP = e?.target?.scrollTop ?? 0;

      if (isClient && SCROLL_TOP < SCROLL_THRESHOLD) {
        const firstGroupId = groups?.[0]?.id;
        if (firstGroupId && firstGroupId !== selectedGroupId) {
          updateSelectedGroupThrottled(firstGroupId || null);
        }
        return;
      }

      for (const groupId in groupRefs.current) {
        if (!groupRefs?.current?.[groupId]) continue;

        const rect = groupRefs.current[groupId].getBoundingClientRect();

        // A group is "at top" when its heading has scrolled to or above the nav-bar
        // and its content still occupies the area just below the nav-bar.
        const isAtTop = rect.top <= SCROLL_THRESHOLD + 4 && rect.bottom > SCROLL_THRESHOLD;

        if (isAtTop && !footerInView) {
          const isSelected = selectedGroupIdRef.current === groupId;
          if (!isSelected) updateSelectedGroupThrottled(groupId);
          break;
        }
      }
    },
    [
      groups,
      selectedGroupId,
      SCROLL_THRESHOLD,
      updateSelectedGroupThrottled,
      footerInView,
      isClient,
    ]
  );

  const handleScrollThrottled = throttle(handleScroll, 200, {
    trailing: true,
  });

  return { groupRefs, handleScrollThrottled };
};
